// hooks/useComplexCartModal.ts
import { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store/store";
import { selectCategoryList } from "../../../store/slices/categoryListSlice";
import {
  clearComplexOrderCart,
  setComplexCartPhone,
  setComplexCartComment,
  setComplexCartFieldValues,
} from "../../../store/slices/complexOrderCartSlice";
import { getApi } from "../../../api/api";
import { ComplexOrderStatus, GoalType } from "../../types/enums";
import { setComplexOrders } from "../../../store/slices/complexOrderListSlice";
import useMetrics from "../../hooks/useMetrics";
import { ComplexOrderItem } from "../../types/types";
import { getErrorMessage } from "../../utils/functions/getErrorMessage";

// ===== ФУНКЦИЯ ДЛЯ ОШИБОК (импорт поправишь сам) =====

interface FieldValue {
  fieldId: number;
  value: string;
}

interface CategoryField {
  id: number;
  name: string;
  type: number;
  isActive: boolean;
  isVisibleForClient: boolean;
  isFillByClient: boolean;
  required: boolean;
  sortOrder: number;
  description?: string;
}

export interface CategoryFieldsGroup {
  categoryId: number;
  categoryName: string;
  fields: CategoryField[];
}

export const useComplexCartModal = (
  onSuccess: (orderNumber: string) => void,
) => {
  const dispatch = useDispatch();
  const { trackGoal } = useMetrics();
  const {
    items,
    phone: storedPhone,
    comment: storedComment,
    fieldValues: storedFieldValues,
  } = useSelector((state: RootState) => state.complexOrderCart);
  const categoryList = useSelector(selectCategoryList);

  const [phone, setPhone] = useState(storedPhone || "");
  const [comment, setComment] = useState(storedComment || "");
  const [fieldValues, setFieldValues] = useState<FieldValue[]>(
    storedFieldValues || [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null); // 👈 Добавляем

  // Сбрасываем ошибку при изменении полей
  useEffect(() => {
    setError(null);
  }, [phone, comment, fieldValues]);

  useEffect(() => {
    dispatch(setComplexCartPhone(phone));
  }, [phone, dispatch]);

  useEffect(() => {
    dispatch(setComplexCartComment(comment));
  }, [comment, dispatch]);

  useEffect(() => {
    dispatch(setComplexCartFieldValues(fieldValues));
  }, [fieldValues, dispatch]);

  const fieldsByCategory = useMemo(() => {
    const result: Record<number, CategoryFieldsGroup> = {};

    items.forEach((item) => {
      const category = categoryList.find((c) => c.id === item.categoryId);
      if (category?.fields?.length && category.isComplex) {
        if (!result[category.id!]) {
          result[category.id!] = {
            categoryId: category.id!,
            categoryName: category.name,
            fields: [],
          };
        }
        category.fields.forEach((field: CategoryField) => {
          if (field.isActive && field.isVisibleForClient) {
            const exists = result[category.id!].fields.find(
              (f) => f.id === field.id,
            );
            if (!exists) {
              result[category.id!].fields.push(field);
            }
          }
        });
      }
    });

    return Object.values(result).sort((a, b) => a.categoryId - b.categoryId);
  }, [items, categoryList]);

  const allFields = useMemo(
    () => fieldsByCategory.flatMap((g) => g.fields),
    [fieldsByCategory],
  );

  const groupedItems = useMemo(() => {
    const parents: ComplexOrderItem[] = [];
    const childrenMap: Record<string, ComplexOrderItem[]> = {};

    items.forEach((item) => {
      if (!item.parentId) {
        parents.push(item);
      } else {
        if (!childrenMap[item.parentId]) {
          childrenMap[item.parentId] = [];
        }
        childrenMap[item.parentId].push(item);
      }
    });

    if (parents.length === 0 && Object.keys(childrenMap).length > 0) {
      const allChildren = Object.values(childrenMap).flat();
      return { parents: allChildren, childrenMap: {} };
    }

    return { parents, childrenMap };
  }, [items]);

  const handleFieldChange = useCallback((fieldId: number, value: string) => {
    setFieldValues((prev) => {
      const existing = prev.find((v) => v.fieldId === fieldId);
      if (existing) {
        return prev.map((v) => (v.fieldId === fieldId ? { ...v, value } : v));
      }
      return [...prev, { fieldId, value }];
    });
  }, []);

  const isPhoneValid = phone.trim().length >= 10;
  const isRequiredFieldsFilled = allFields
    .filter((f) => f.required && f.isFillByClient)
    .every((f) => {
      const val = fieldValues.find((v) => v.fieldId === f.id);
      return val && val.value.trim();
    });

  const disabled = !isPhoneValid || !isRequiredFieldsFilled || loading;

  const total = useMemo(
    () => items.reduce((sum, item) => sum + item.total, 0),
    [items],
  );

  const handleSend = useCallback(async () => {
    if (disabled) return;

    setLoading(true);
    setError(null); // 👈 Сбрасываем ошибку перед отправкой

    try {
      const api = getApi();
      const result = await api.createComplexOrder({
        status: ComplexOrderStatus.DRAFT,
        total,
        phone_number: phone,
        notes: comment || undefined,
        items: items.map((item) => ({
          itemId: item.itemId,
          name: item.name,
          quantity: item.quantity,
          basePrice: item.price,
          total: item.total,
          parentId: item.parentId || null,
          unit: item.unit,
        })),
        data: fieldValues
          .filter((fv) => fv.value)
          .map((fv) => ({
            fieldTemplateId: fv.fieldId,
            type: "custom",
            value: fv.value,
          })),
      });

      dispatch(clearComplexOrderCart());

      try {
        const ordersResult = await api.getComplexOrderList();
        if (ordersResult) {
          dispatch(
            setComplexOrders(Array.isArray(ordersResult) ? ordersResult : []),
          );
        }
      } catch (e) {
        console.error("Failed to refresh complex orders:", e);
      }

      onSuccess(String(result.id));
      trackGoal(GoalType.PROJECT_ORDER, String(result.id), {
        total,
        items: items.length,
      });
    } catch (error: any) {
      console.error("Failed to send complex order:", error);
      setError(getErrorMessage(error)); // 👈 Показываем ошибку
    } finally {
      setLoading(false);
    }
  }, [
    items,
    phone,
    comment,
    fieldValues,
    total,
    disabled,
    dispatch,
    onSuccess,
  ]);

  return {
    fieldsByCategory,
    fieldValues,
    phone,
    comment,
    loading,
    disabled,
    total,
    groupedItems,
    error, // 👈 Возвращаем ошибку
    setPhone,
    setComment,
    handleFieldChange,
    handleSend,
  };
};
